import { motion, AnimatePresence } from 'framer-motion';
import { Play, Pause, Volume2, VolumeX, Radio, Music, X, ExternalLink, Minimize2, Maximize2, Loader2, SkipForward, SkipBack, Youtube, Headphones, Video } from 'lucide-react';
import { useAudio } from '../contexts/AudioContext';
import { useQueue } from '../contexts/QueueContext';
import QueueDrawer from './QueueDrawer';
import { thumbUrl } from '../lib/imageUrl';
import { useState, useEffect, useCallback } from 'react';

const AudioPlayer = () => {
  const {
    isPlaying,
    isLoading,
    currentTrack,
    volume,
    showPlayer,
    useEmbedPlayer,
    embedUrl,
    embedLoaded,
    activeSource,
    playbackError,
    playerPresentation,
    mediaViewMode,
    audioRef,
    videoRef,
    togglePlay,
    playRadio,
    playContent,
    setVolume,
    setShowPlayer,
    setPlayerPresentation,
    setMediaViewMode,
    onEmbedLoaded,
    onMediaPlaying,
    onMediaPause,
    onMediaWaiting,
    stopAll
  } = useAudio();

  const {
    queue,
    currentIndex,
    autoplay,
    advanceOrAutoplay,
    getNextTrack,
    getPrevTrack,
    setPlayingIndex,
  } = useQueue();

  const [showVolumeSlider, setShowVolumeSlider] = useState(false);
  const [embedExpanded, setEmbedExpanded] = useState(false);
  const minimized = playerPresentation === 'minimized';

  const playQueueTrack = useCallback(async (track) => {
    if (!track) return;
    playContent(track);
  }, [playContent]);

  const continueQueue = useCallback(async () => {
    const next = await advanceOrAutoplay();
    if (next) {
      await playQueueTrack(next);
      return true;
    }
    playRadio();
    return false;
  }, [advanceOrAutoplay, playQueueTrack, playRadio]);

  useEffect(() => {
    const audio = audioRef?.current;
    const video = videoRef?.current;
    const handleTrackEnded = () => { continueQueue(); };
    audio?.addEventListener('ended', handleTrackEnded);
    video?.addEventListener('ended', handleTrackEnded);
    return () => {
      audio?.removeEventListener('ended', handleTrackEnded);
      video?.removeEventListener('ended', handleTrackEnded);
    };
  }, [activeSource, audioRef, continueQueue, videoRef]);

  useEffect(() => {
    const handleYtEnd = () => { continueQueue(); };
    window.addEventListener('youtube-track-ended', handleYtEnd);
    return () => window.removeEventListener('youtube-track-ended', handleYtEnd);
  }, [continueQueue]);

  const handleSkipNext = useCallback(() => {
    continueQueue();
  }, [continueQueue]);

  const handleSkipPrev = useCallback(() => {
    const prev = getPrevTrack();
    if (prev) {
      setPlayingIndex(currentIndex - 1);
      playQueueTrack(prev);
    }
  }, [getPrevTrack, currentIndex, setPlayingIndex, playQueueTrack]);

  const handleVolumeChange = (e) => {
    setVolume(parseFloat(e.target.value));
  };

  const toggleMute = () => {
    setVolume(volume > 0 ? 0 : 0.7);
  };

  if (!showPlayer) return null;

  const isYouTube = activeSource === 'youtube' || currentTrack.type === 'youtube';
  const isVideo = activeSource === 'video' || currentTrack.type === 'video';
  const isLive = currentTrack.type === 'radio' || currentTrack.type === 'creator-radio' || currentTrack.track_type === 'radio';

  return (
    <AnimatePresence>
      <motion.div
        initial={{ y: 100, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: 100, opacity: 0 }}
        className={`fixed left-0 right-0 z-40 backdrop-blur-xl bg-black/90 border-t border-white/10 bottom-[calc(4rem+env(safe-area-inset-bottom,0px))] lg:!bottom-0 ${
          useEmbedPlayer && embedExpanded ? 'pb-0' : ''
        } ${minimized ? 'shadow-[0_-8px_24px_rgba(0,0,0,0.45)]' : ''}`}
        data-testid="audio-player"
      >
        <div className={`max-w-7xl mx-auto px-3 sm:px-4 ${minimized ? 'py-1.5' : 'py-3'}`}>
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center space-x-4 flex-1 min-w-0">
              <div className={`relative flex-shrink-0 rounded-sm overflow-hidden group ${minimized ? 'h-10 w-10' : 'h-14 w-14'}`}>
                <img
                  src={thumbUrl(currentTrack.cover)}
                  alt={currentTrack.title}
                  className="w-full h-full object-cover"
                  loading="lazy"
                  decoding="async"
                />
                {isLoading && (
                  <div className="absolute inset-0 bg-black/70 flex items-center justify-center">
                    <Loader2 className="w-6 h-6 text-primary animate-spin" />
                  </div>
                )}
                {!isLoading && isLive && (
                  <div className="absolute inset-0 bg-black/60 flex items-center justify-center">
                    <Radio className="w-6 h-6 text-primary" />
                  </div>
                )}
                {!isLoading && isYouTube && (
                  <div className="absolute inset-0 bg-black/60 flex items-center justify-center">
                    <Youtube className="w-6 h-6 text-red-500" />
                  </div>
                )}
                {!isLoading && !isYouTube && useEmbedPlayer && (
                  <div className="absolute inset-0 bg-black/60 flex items-center justify-center">
                    <Music className="w-6 h-6 text-primary" />
                  </div>
                )}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center space-x-2 mb-1">
                  <h3 className="font-heading font-semibold text-sm truncate" data-testid="track-title">
                    {currentTrack.title}
                  </h3>
                  {isLoading && (
                    <span className="px-2 py-0.5 bg-primary/20 text-primary text-xs font-bold rounded-sm flex-shrink-0 flex items-center gap-1">
                      <Loader2 className="w-3 h-3 animate-spin" /> Loading...
                    </span>
                  )}
                  {!isLoading && isLive && isPlaying && (
                    <span className="px-2 py-0.5 bg-destructive text-white text-xs font-bold rounded-sm animate-pulse flex-shrink-0">LIVE</span>
                  )}
                  {!isLoading && isYouTube && isPlaying && (
                    <span className="px-2 py-0.5 bg-red-600 text-white text-xs font-bold rounded-sm flex-shrink-0">YOUTUBE</span>
                  )}
                  {!isLoading && !isYouTube && useEmbedPlayer && isPlaying && (
                    <span className="px-2 py-0.5 bg-primary text-black text-xs font-bold rounded-sm flex-shrink-0">FULL TRACK</span>
                  )}
                </div>
                <p className="text-xs text-muted-foreground truncate" data-testid="track-artist">
                  {currentTrack.artist}
                </p>
              </div>
            </div>

            <div className="flex items-center space-x-3">
              {(isYouTube || isVideo) && !minimized && (
                <button
                  type="button"
                  onClick={() => setMediaViewMode(mediaViewMode === 'video' ? 'audio' : 'video')}
                  className="inline-flex items-center gap-1 rounded-sm border border-white/10 px-2 py-2 text-[10px] font-bold uppercase text-zinc-200 hover:border-primary/40"
                  title={mediaViewMode === 'video' ? 'Listen with the video minimized' : 'Show the video'}
                  data-testid="toggle-media-view-mode"
                >
                  {mediaViewMode === 'video' ? <Headphones className="h-4 w-4" /> : <Video className="h-4 w-4" />}
                  <span className="hidden sm:inline">{mediaViewMode === 'video' ? 'Audio only' : 'Watch video'}</span>
                </button>
              )}

              <button
                type="button"
                onClick={() => setPlayerPresentation(minimized ? 'expanded' : 'minimized')}
                className="rounded-sm border border-white/10 p-2 text-zinc-200 hover:border-primary/40"
                title={minimized ? 'Restore player and listening shortcuts' : 'Minimize player and listening shortcuts'}
                aria-label={minimized ? 'Restore player and listening shortcuts' : 'Minimize player and listening shortcuts'}
                data-testid="toggle-player-presentation"
              >
                {minimized ? <Maximize2 className="h-4 w-4" /> : <Minimize2 className="h-4 w-4" />}
              </button>
              {!isLive && (
                <button
                  onClick={playRadio}
                  data-testid="switch-to-radio"
                  className="p-2 hover:bg-white/10 rounded-sm transition-colors duration-300 hidden sm:flex items-center space-x-2"
                  title="Switch to Radio"
                >
                  <Radio className="w-5 h-5" />
                  <span className="text-xs">Radio</span>
                </button>
              )}

              {currentTrack.externalUrl && (isYouTube || isVideo) && (
                <a
                  href={currentTrack.externalUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="open-external"
                  className="p-2 hover:bg-white/10 rounded-sm transition-colors duration-300 hidden sm:block"
                  title={isYouTube ? 'Open on YouTube' : 'Open original video'}
                >
                  <ExternalLink className="w-4 h-4" />
                </a>
              )}

              {useEmbedPlayer && !isYouTube && (
                <button
                  onClick={() => setEmbedExpanded(!embedExpanded)}
                  data-testid="toggle-embed-size"
                  className="p-2 hover:bg-white/10 rounded-sm transition-colors duration-300 hidden sm:block"
                  title={embedExpanded ? 'Minimize Player' : 'Expand Player'}
                >
                  {embedExpanded ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
                </button>
              )}

              {queue.length > 0 && (
                <button
                  onClick={handleSkipPrev}
                  disabled={currentIndex <= 0}
                  data-testid="skip-prev-btn"
                  className="p-2 hover:bg-white/10 rounded-sm transition-colors duration-300 hidden sm:block disabled:opacity-30"
                  title="Previous"
                >
                  <SkipBack className="w-4 h-4" />
                </button>
              )}

              <button
                onClick={playbackError && isYouTube ? () => playContent(currentTrack) : togglePlay}
                disabled={isLoading}
                data-testid="play-pause-button"
                className={`w-10 h-10 rounded-full flex items-center justify-center transition-all duration-300 ${
                  isLoading ? 'bg-primary/50 cursor-wait' : 'bg-primary hover:bg-primary/90 neon-gold-shadow'
                }`}
              >
                {isLoading ? (
                  <Loader2 className="w-5 h-5 text-black animate-spin" />
                ) : isPlaying ? (
                  <Pause className="w-5 h-5 text-black" />
                ) : (
                  <Play className="w-5 h-5 text-black ml-0.5" />
                )}
              </button>

              {queue.length > 0 && (
                <button
                  onClick={handleSkipNext}
                  disabled={!getNextTrack() && !autoplay}
                  data-testid="skip-next-btn"
                  className="p-2 hover:bg-white/10 rounded-sm transition-colors duration-300 hidden sm:block disabled:opacity-30"
                  title={getNextTrack() ? 'Next' : 'Continue with Autoplay'}
                >
                  <SkipForward className="w-4 h-4" />
                </button>
              )}

              <div className="hidden md:flex items-center space-x-2">
                <button
                  onClick={toggleMute}
                  onMouseEnter={() => setShowVolumeSlider(true)}
                  data-testid="volume-button"
                  className="p-2 hover:bg-white/10 rounded-sm transition-colors duration-300"
                >
                  {volume === 0 ? <VolumeX className="w-5 h-5" /> : <Volume2 className="w-5 h-5" />}
                </button>
                {showVolumeSlider && (
                  <motion.div
                    initial={{ width: 0, opacity: 0 }}
                    animate={{ width: 80, opacity: 1 }}
                    onMouseLeave={() => setShowVolumeSlider(false)}
                    className="flex items-center"
                  >
                    <input
                      type="range" min="0" max="1" step="0.01"
                      value={volume} onChange={handleVolumeChange}
                      data-testid="volume-slider"
                      className="w-full h-1 bg-white/20 rounded-lg appearance-none cursor-pointer"
                      style={{ background: `linear-gradient(to right, #FFB800 0%, #FFB800 ${volume * 100}%, rgba(255,255,255,0.2) ${volume * 100}%, rgba(255,255,255,0.2) 100%)` }}
                    />
                  </motion.div>
                )}
              </div>

              <div className="relative hidden sm:block">
                <QueueDrawer onPlayTrack={playQueueTrack} />
              </div>

              <button
                onClick={() => { stopAll(); setShowPlayer(false); }}
                data-testid="close-player"
                className="p-2 hover:bg-white/10 rounded-sm transition-colors duration-300 hidden sm:block"
                title="Close Player"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>

          {!minimized && isLoading && activeSource === 'audio' && (
            <div className="mt-2">
              <div className="h-1 bg-white/10 rounded-full overflow-hidden">
                <motion.div className="h-full bg-primary" initial={{ width: '0%' }} animate={{ width: '100%' }} transition={{ duration: 2, ease: 'easeInOut' }} />
              </div>
              <p className="text-xs text-muted-foreground mt-1 text-center">Loading track...</p>
            </div>
          )}

          {!minimized && !isLoading && isLive && isPlaying && activeSource === 'audio' && (
            <div className="mt-2">
              <div className="h-0.5 bg-white/10 rounded-full overflow-hidden">
                <motion.div className="h-full bg-primary" animate={{ x: ['0%', '100%'] }} transition={{ duration: 2, repeat: Infinity, ease: 'linear' }} style={{ width: '30%' }} />
              </div>
            </div>
          )}

          {!minimized && !isLoading && isYouTube && isPlaying && (
            <div className="mt-2">
              <div className="h-0.5 bg-white/10 rounded-full overflow-hidden">
                <motion.div className="h-full bg-red-500" animate={{ x: ['0%', '100%'] }} transition={{ duration: 3, repeat: Infinity, ease: 'linear' }} style={{ width: '40%' }} />
              </div>
            </div>
          )}
        </div>

        {useEmbedPlayer && embedUrl && !isYouTube && !minimized && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: embedExpanded ? 200 : 120, opacity: 1 }}
            transition={{ duration: 0.3 }}
            className="overflow-hidden bg-black/50 relative"
          >
            {!embedLoaded && (
              <div className="absolute inset-0 flex items-center justify-center bg-black/80 z-10">
                <div className="flex flex-col items-center gap-2">
                  <Loader2 className="w-8 h-8 text-primary animate-spin" />
                  <p className="text-sm text-muted-foreground">Loading player...</p>
                </div>
              </div>
            )}
            <iframe
              title={`${currentTrack.provider || 'Creator'} media player`}
              src={embedUrl}
              width="100%"
              height={embedExpanded ? '200' : '120'}
              scrolling="no"
              frameBorder="0"
              allow="autoplay; encrypted-media"
              style={{ border: 'none' }}
              data-testid="hearthis-embed-player"
              onLoad={onEmbedLoaded}
            />
          </motion.div>
        )}

        {isYouTube && (
          <div className={mediaViewMode === 'video' && !minimized ? 'bg-black px-3 pb-3 sm:px-4 sm:pb-4' : 'fixed -left-[9999px] h-px w-px overflow-hidden opacity-0'} data-testid="youtube-video-surface" data-view-mode={mediaViewMode}>
            <div className="mx-auto aspect-video max-h-[46vh] w-full max-w-3xl overflow-hidden rounded-sm bg-black [&>iframe]:h-full [&>iframe]:w-full">
              <div id="yt-playblock-player" className="h-full w-full" />
            </div>
            {playbackError && (
              <p className="mx-auto mt-2 max-w-3xl rounded-sm border border-red-500/25 bg-red-500/10 px-3 py-2 text-xs text-red-200" role="alert">
                {playbackError}
              </p>
            )}
          </div>
        )}

        {isVideo && currentTrack.url && (
          <div className={mediaViewMode === 'video' && !minimized ? 'bg-black/80 px-4 pb-4' : 'fixed -left-[9999px] h-px w-px overflow-hidden opacity-0'} data-testid="direct-video-surface" data-view-mode={mediaViewMode}>
            <video
              ref={videoRef}
              src={currentTrack.url}
              poster={currentTrack.cover || undefined}
              controls
              playsInline
              className="mx-auto max-h-64 w-full max-w-3xl rounded-sm bg-black"
              data-testid="creator-video-player"
              onCanPlay={onEmbedLoaded}
              onPlaying={onMediaPlaying}
              onPause={onMediaPause}
              onWaiting={onMediaWaiting}
            />
          </div>
        )}

        {activeSource === 'audio' && (
          <audio
            ref={audioRef}
            src={currentTrack.url}
            preload="metadata"
          />
        )}
        {activeSource !== 'audio' && <audio ref={audioRef} preload="none" />}
        {!isVideo && <video ref={videoRef} className="hidden" preload="none" />}
      </motion.div>
    </AnimatePresence>
  );
};

export default AudioPlayer;
